/**
 * The window's side of Nova's playout (apps/playout, Rust): On air played outside the
 * window. One WebSocket, protobuf both ways, from the shared contract
 * (proto/nova/playout/v1/playout.proto; the types in ./gen come from it: npm run proto).
 *
 * Phases A-C (2026-11-02): the client and the contract. On air still plays in the window
 * (onAir.ts) until the switch-over; this is what it will drive. The playout keeps the
 * queue (`queue()`, sent whole when it changes) and, run with --station, the station's
 * brain (`station()`: modes, Auto DJ, fixed times, live shows).
 *
 *   const p = connectPlayout("ws://127.0.0.1:8788", token);
 *   p.subscribe(() => render(p.state(), p.meter()));
 *   await p.send({ case: "play", value: create(PlaySchema) });
 */

import { create, fromBinary, toBinary, type MessageInitShape } from "@bufbuild/protobuf";
import {
  CommandSchema,
  EventSchema,
  ItemSchema,
  TrackSchema,
  type Event,
  type Item,
  type Meter,
  type Outputs,
  type Played,
  type Presenters,
  type Queue,
  type RtcSignal,
  type Reply,
  type Started,
  type State,
  type Station,
  type Track,
} from "./gen/nova/playout/v1/playout_pb";
import type { QueueItem, QueueTrack } from "./player";
import { linkInfo, onLinkInfo, onMessage as onLinkMessage, openLink, send as linkSend } from "./link";

/** One command: the `cmd` oneof of Command, plain values allowed (its id is filled in here). */
export type PlayoutCommand = MessageInitShape<typeof CommandSchema>["cmd"];

export interface PlayoutClient {
  /** The playout's state as last heard, or null before the first. */
  state(): State | null;
  meter(): Meter | null;
  outputs(): Outputs | null;
  /** The queue as the playout holds it (it changes it too: Auto DJ, fixed times). */
  queue(): Queue | null;
  /** The station's brain, when the playout runs it (--station). */
  station(): Station | null;
  /** Remote Studio's presenters connected to the playout (phase D). */
  presenters(): Presenters | null;
  connected(): boolean;
  /** Called on every state, meter, queue, station or connection change. */
  subscribe(fn: () => void): () => void;
  /** An item began to sound (the play log). */
  onStarted(fn: (s: Started) => void): () => void;
  /** Something finished sounding (song, cart, clip over a song): the play log. */
  onPlayed(fn: (p: Played) => void): () => void;
  /** Every event as it comes (anything not kept above: skipped, trimmed, notices, inputs). */
  onEvent(fn: (e: Event["event"]) => void): () => void;
  /** For a presenter, to relay on the live link: rtc.answer, rtc.ice or rtc.close. */
  onSignal(fn: (s: RtcSignal) => void): () => void;
  /** Connect to the playout at a new address (it was started again elsewhere). */
  retarget(url: string, token: string): void;
  /** Send one command; resolves with the playout's reply (ok, or why not). */
  send(cmd: PlayoutCommand): Promise<Reply>;
  close(): void;
}

/** Long enough for a sound card to change; a reply is normally back in milliseconds. */
const REPLY_MS = 5000;
const RETRY_MS = [250, 500, 1000, 2000, 5000];

/** Something that opens WebSockets (the window's own; tests pass a fake). */
export type SocketFactory = (url: string) => WebSocket;

function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
function fromBase64(text: string): ArrayBuffer {
  const s = atob(text);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out.buffer;
}

/**
 * The playout reached through the engine's live link instead of its own port (a desk
 * over the network, or a window that cannot reach 127.0.0.1): it is the station there
 * (apps/playout/src/link.rs). Stands in for a WebSocket, so `connectPlayout` works the
 * same either way: `connectPlayout("link", "", linkPlayoutSocket)`. Its frames travel as
 * `po.cmd` / `po` / `po.one` with the same protobuf inside; it is open while the link is
 * and the playout is the station on it, and closes (the client then tries again) when
 * either goes.
 */
export const linkPlayoutSocket: SocketFactory = () => {
  const offs: (() => void)[] = [];
  const sock = {
    binaryType: "arraybuffer",
    readyState: 0,
    onopen: null as (() => void) | null,
    onmessage: null as ((m: { data: ArrayBuffer }) => void) | null,
    onclose: null as (() => void) | null,
    send(bytes: Uint8Array) {
      if (sock.readyState === 1) linkSend({ type: "po.cmd", b: toBase64(bytes) });
    },
    close() {
      if (sock.readyState === 3) return;
      sock.readyState = 3;
      offs.forEach((off) => off());
      queueMicrotask(() => sock.onclose?.());
    },
  };
  const deliver = (m: { [key: string]: unknown }) => {
    if (sock.readyState === 1 && typeof m.b === "string") sock.onmessage?.({ data: fromBase64(m.b) });
  };
  const check = () => {
    const i = linkInfo();
    const up = i.connected && i.playout;
    if (up && sock.readyState === 0) {
      sock.readyState = 1;
      sock.onopen?.();
      linkSend({ type: "po.hello" });
    } else if (!up && sock.readyState === 1) sock.close();
  };
  offs.push(onLinkMessage("po", deliver), onLinkMessage("po.one", deliver), onLinkInfo(check));
  openLink();
  setTimeout(check, 0);
  return sock as unknown as WebSocket;
};

export function connectPlayout(firstUrl: string, firstToken: string, open: SocketFactory = (u) => new WebSocket(u)): PlayoutClient {
  let url = firstUrl;
  let token = firstToken;
  let ws: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let state: State | null = null;
  let meter: Meter | null = null;
  let outputs: Outputs | null = null;
  let queue: Queue | null = null;
  let station: Station | null = null;
  let presenters: Presenters | null = null;
  let nextId = 1;
  const waiting = new Map<bigint, { resolve: (r: Reply) => void; timer: ReturnType<typeof setTimeout> }>();
  const listeners = new Set<() => void>();
  const started = new Set<(s: Started) => void>();
  const played = new Set<(p: Played) => void>();
  const signals = new Set<(s: RtcSignal) => void>();
  const every = new Set<(e: Event["event"]) => void>();
  const changed = () => listeners.forEach((fn) => fn());

  const connect = () => {
    if (closed) return;
    const target = `${url}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
    const socket = open(target);
    socket.binaryType = "arraybuffer";
    ws = socket;
    socket.onopen = () => {
      attempt = 0;
      changed();
    };
    socket.onmessage = (m) => {
      if (!(m.data instanceof ArrayBuffer)) return;
      const ev = fromBinary(EventSchema, new Uint8Array(m.data)).event;
      every.forEach((fn) => fn(ev));
      switch (ev.case) {
        case "state":
          state = ev.value;
          break;
        case "meter":
          meter = ev.value;
          break;
        case "outputs":
          outputs = ev.value;
          break;
        case "queue":
          queue = ev.value;
          break;
        case "station":
          station = ev.value;
          break;
        case "played":
          played.forEach((fn) => fn(ev.value));
          return;
        case "presenters":
          presenters = ev.value;
          break;
        case "rtcSignal":
          signals.forEach((fn) => fn(ev.value));
          return;
        case "started":
          started.forEach((fn) => fn(ev.value));
          return;
        case "reply": {
          const w = waiting.get(ev.value.id);
          if (w) {
            clearTimeout(w.timer);
            waiting.delete(ev.value.id);
            w.resolve(ev.value);
          }
          return;
        }
        default:
          return;
      }
      changed();
    };
    socket.onclose = () => {
      if (ws === socket) ws = null;
      changed();
      if (closed) return;
      const wait = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];
      attempt += 1;
      setTimeout(connect, wait);
    };
  };
  connect();

  return {
    state: () => state,
    meter: () => meter,
    outputs: () => outputs,
    queue: () => queue,
    station: () => station,
    presenters: () => presenters,
    connected: () => ws?.readyState === 1,
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    onStarted(fn) {
      started.add(fn);
      return () => {
        started.delete(fn);
      };
    },
    onPlayed(fn) {
      played.add(fn);
      return () => {
        played.delete(fn);
      };
    },
    onEvent(fn) {
      every.add(fn);
      return () => {
        every.delete(fn);
      };
    },
    onSignal(fn) {
      signals.add(fn);
      return () => {
        signals.delete(fn);
      };
    },
    send(cmd) {
      const id = BigInt(nextId++);
      return new Promise<Reply>((resolve) => {
        const fail = (error: string) => resolve({ $typeName: "nova.playout.v1.Reply", id, ok: false, error });
        if (!ws || ws.readyState !== 1) return fail("the playout is not connected");
        const timer = setTimeout(() => {
          waiting.delete(id);
          fail("the playout did not answer");
        }, REPLY_MS);
        waiting.set(id, { resolve, timer });
        ws.send(toBinary(CommandSchema, create(CommandSchema, { id, cmd })));
      });
    },
    retarget(nextUrl, nextToken) {
      if (nextUrl === url && nextToken === token) return;
      url = nextUrl;
      token = nextToken;
      attempt = 0;
      // Its close starts the next connection, to the new address.
      ws?.close();
    },
    close() {
      closed = true;
      ws?.close();
      for (const [, w] of waiting) clearTimeout(w.timer);
      waiting.clear();
    },
  };
}

/** A window queue track as the contract has it. */
export function toPlayoutTrack(t: QueueTrack): Track {
  return create(TrackSchema, {
    id: t.id,
    title: t.title,
    artist: t.artist,
    duration: t.duration,
    cueIn: t.cueIn ?? undefined,
    cueOut: t.cueOut ?? undefined,
    mixAt: t.mixAt ?? undefined,
    lufs: t.lufs ?? undefined,
    peak: t.peak ?? undefined,
    album: t.album ?? "",
    year: t.year ?? undefined,
    bpm: t.bpm ?? undefined,
    musicalKey: t.musicalKey ?? "",
    introEnd: t.introEnd ?? undefined,
  });
}

/** A window queue item as the contract has it. */
export function toPlayoutItem(q: QueueItem): Item {
  return create(ItemSchema, {
    key: q.key,
    hold: !!q.hold,
    fadeOut: q.fadeOut,
    track: toPlayoutTrack(q.track),
    overlays: (q.overlays ?? []).map((o) => ({ track: toPlayoutTrack(o.track), at: o.at, reason: o.reason ?? "", placement: o.placement ?? "" })),
    reason: q.reason ?? "",
    auto: !!q.auto,
    pinned: !!q.pinned,
    from: q.from ?? "",
    trimmed: q.trimmed,
  });
}
